import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, addDoc, updateDoc, deleteDoc, doc, serverTimestamp, Timestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';

interface VenueEvent {
  id: string;
  venueId: string;
  title: string;
  description: string;
  eventType: 'tournament' | 'special' | 'promotion';
  startDate: any;
  endDate: any;
  imageUrl?: string;
  isActive: boolean;
  createdAt: any;
}

interface VenuePost {
  id: string;
  venueId: string;
  title: string;
  content: string;
  imageUrl?: string;
  isActive: boolean;
  createdAt: any;
}

const VenueCustomization: React.FC = () => {
  const { user, isVenueOperator } = useAuth();
  const { t } = useTranslation();
  const [selectedVenueId, setSelectedVenueId] = useState<string>('');
  const [venueName, setVenueName] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'image' | 'events' | 'posts'>('image');

  // Main image
  const [mainImageUrl, setMainImageUrl] = useState<string>('');
  const [uploadingImage, setUploadingImage] = useState(false);

  // Events
  const [events, setEvents] = useState<VenueEvent[]>([]);
  const [showEventModal, setShowEventModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState<string | null>(null);
  const [eventForm, setEventForm] = useState({
    title: '',
    description: '',
    eventType: 'tournament' as 'tournament' | 'special' | 'promotion',
    startDate: '',
    endDate: '',
    imageUrl: '',
    // Tournament specific fields
    registrationDeadline: '',
    tournamentType: 'single_elimination' as 'single_elimination' | 'double_elimination' | 'round_robin' | 'swiss',
    gameMode: '501' as '301' | '501' | 'cricket' | 'custom',
    maxParticipants: 16,
    prizePool: 0,
    entryFee: 0,
    rules: '',
    // Prizes
    firstPrize: '',
    secondPrize: '',
    thirdPrize: ''
  });

  // Posts
  const [posts, setPosts] = useState<VenuePost[]>([]);
  const [showPostModal, setShowPostModal] = useState(false);
  const [editingPost, setEditingPost] = useState<string | null>(null);
  const [postForm, setPostForm] = useState({
    title: '',
    content: '',
    imageUrl: ''
  });

  useEffect(() => {
    const venueId = localStorage.getItem('selectedVenueId');
    if (venueId) {
      setSelectedVenueId(venueId);
      loadVenueData(venueId);
    } else if (user && isVenueOperator) {
      loadFirstVenue();
    }
  }, [user, isVenueOperator]);

  const loadFirstVenue = async () => {
    if (!user?.uid) return;

    try {
      const venuesQuery = query(
        collection(db, 'venues'),
        where('operatorId', '==', user.uid),
        where('isActive', '==', true)
      );
      const venuesSnapshot = await getDocs(venuesQuery);

      if (!venuesSnapshot.empty) {
        const firstVenue = venuesSnapshot.docs[0];
        setSelectedVenueId(firstVenue.id);
        loadVenueData(firstVenue.id);
      }
    } catch (error) {
      console.error('Error loading venue:', error);
    }
  };

  const loadVenueData = async (venueId: string) => {
    setLoading(true);
    try {
      // Load venue basic info
      const venueDoc = await getDocs(query(collection(db, 'venues'), where('__name__', '==', venueId)));
      if (!venueDoc.empty) {
        const venueData = venueDoc.docs[0].data();
        setVenueName(venueData.name || '');
        setMainImageUrl(venueData.mainImageUrl || '');
      }

      // Load events
      await loadEvents(venueId);

      // Load posts
      await loadPosts(venueId);
    } catch (error) {
      console.error('Error loading venue data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadEvents = async (venueId: string) => {
    try {
      const eventsQuery = query(
        collection(db, 'venueEvents'),
        where('venueId', '==', venueId),
        where('isActive', '==', true)
      );
      const eventsSnapshot = await getDocs(eventsQuery);

      const eventsData: VenueEvent[] = [];
      eventsSnapshot.forEach((doc) => {
        eventsData.push({ id: doc.id, ...doc.data() } as VenueEvent);
      });

      setEvents(eventsData);
    } catch (error) {
      console.error('Error loading events:', error);
    }
  };

  const loadPosts = async (venueId: string) => {
    try {
      const postsQuery = query(
        collection(db, 'venuePosts'),
        where('venueId', '==', venueId),
        where('isActive', '==', true)
      );
      const postsSnapshot = await getDocs(postsQuery);

      const postsData: VenuePost[] = [];
      postsSnapshot.forEach((doc) => {
        postsData.push({ id: doc.id, ...doc.data() } as VenuePost);
      });

      setPosts(postsData);
    } catch (error) {
      console.error('Error loading posts:', error);
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !selectedVenueId || !user?.uid) {
      alert('Hiányzó adatok a feltöltéshez.');
      return;
    }

    setUploadingImage(true);
    try {
      // Get file extension
      const fileExtension = file.name.split('.').pop() || 'jpg';

      // Use user UID in path to work with default storage rules
      const storagePath = `venue-images/${user.uid}/${selectedVenueId}-main-${Date.now()}.${fileExtension}`;
      console.log('Uploading to:', storagePath);

      const storageRef = ref(storage, storagePath);
      await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(storageRef);

      await updateDoc(doc(db, 'venues', selectedVenueId), {
        mainImageUrl: downloadURL,
        updatedAt: serverTimestamp()
      });

      setMainImageUrl(downloadURL);
      alert('Főkép sikeresen feltöltve!');
    } catch (error: any) {
      console.error('Error uploading image:', error);
      alert(`Hiba történt a kép feltöltése során: ${error.message || 'Ismeretlen hiba'}`);
    } finally {
      setUploadingImage(false);
    }
  };

  const handleEventSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!eventForm.title || !eventForm.startDate || !eventForm.endDate) {
      alert('Cím, kezdő és befejező dátum megadása kötelező!');
      return;
    }

    try {
      const eventData = {
        venueId: selectedVenueId,
        title: eventForm.title,
        description: eventForm.description,
        eventType: eventForm.eventType,
        startDate: new Date(eventForm.startDate),
        endDate: new Date(eventForm.endDate),
        imageUrl: eventForm.imageUrl,
        isActive: true,
        createdAt: serverTimestamp()
      };

      let venueEventId = editingEvent;

      if (editingEvent) {
        await updateDoc(doc(db, 'venueEvents', editingEvent), {
          ...eventData,
          updatedAt: serverTimestamp()
        });
      } else {
        const venueEventRef = await addDoc(collection(db, 'venueEvents'), eventData);
        venueEventId = venueEventRef.id;
      }

      // If this is a tournament event, create a real tournament
      if (eventForm.eventType === 'tournament') {
        await createTournamentFromEvent(venueEventId!);
      }

      setShowEventModal(false);
      setEditingEvent(null);
      resetEventForm();
      await loadEvents(selectedVenueId);
      alert(eventForm.eventType === 'tournament'
        ? 'Esemény és verseny sikeresen létrehozva!'
        : 'Esemény sikeresen mentve!');
    } catch (error) {
      console.error('Error saving event:', error);
      alert('Hiba történt az esemény mentése során.');
    }
  };

  const createTournamentFromEvent = async (venueEventId: string) => {
    try {
      // Get venue data for venueName
      const venueDoc = await getDocs(query(collection(db, 'venues'), where('__name__', '==', selectedVenueId)));
      const venueData = venueDoc.docs[0]?.data();
      const venueName = venueData?.name || 'Unknown Venue';

      // Use provided registration deadline or calculate (3 days before start date)
      const startDate = new Date(eventForm.startDate);
      let registrationDeadline: Date;

      if (eventForm.registrationDeadline) {
        registrationDeadline = new Date(eventForm.registrationDeadline);
      } else {
        registrationDeadline = new Date(startDate);
        registrationDeadline.setDate(registrationDeadline.getDate() - 3);
      }

      const tournamentData = {
        name: eventForm.title,
        type: eventForm.tournamentType,
        gameMode: eventForm.gameMode,
        status: 'draft' as const,
        maxParticipants: eventForm.maxParticipants,
        currentParticipants: 0,
        registrationDeadline: Timestamp.fromDate(registrationDeadline),
        startDate: Timestamp.fromDate(new Date(eventForm.startDate)),
        endDate: Timestamp.fromDate(new Date(eventForm.endDate)),
        venueId: selectedVenueId,
        venueName: venueName,
        prizePool: eventForm.prizePool,
        entryFee: eventForm.entryFee,
        rules: eventForm.rules || eventForm.description,
        organizer: user?.displayName || user?.email || 'Venue Operator',
        // Prizes (optional, can be set later)
        firstPrize: eventForm.firstPrize || null,
        secondPrize: eventForm.secondPrize || null,
        thirdPrize: eventForm.thirdPrize || null,
        // Special fields for venue-specific tournaments
        isVenueEvent: true,
        venueEventId: venueEventId,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      };

      await addDoc(collection(db, 'tournaments'), tournamentData);
      console.log('Tournament created from venue event:', venueEventId);
    } catch (error) {
      console.error('Error creating tournament from event:', error);
      throw error;
    }
  };

  const resetEventForm = () => {
    setEventForm({
      title: '',
      description: '',
      eventType: 'tournament',
      startDate: '',
      endDate: '',
      imageUrl: '',
      registrationDeadline: '',
      tournamentType: 'single_elimination',
      gameMode: '501',
      maxParticipants: 16,
      prizePool: 0,
      entryFee: 0,
      rules: '',
      firstPrize: '',
      secondPrize: '',
      thirdPrize: ''
    });
  };

  const handlePostSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!postForm.title || !postForm.content) {
      alert('Cím és tartalom megadása kötelező!');
      return;
    }

    try {
      const postData = {
        venueId: selectedVenueId,
        title: postForm.title,
        content: postForm.content,
        imageUrl: postForm.imageUrl,
        isActive: true,
        createdAt: serverTimestamp()
      };

      if (editingPost) {
        await updateDoc(doc(db, 'venuePosts', editingPost), {
          ...postData,
          updatedAt: serverTimestamp()
        });
      } else {
        await addDoc(collection(db, 'venuePosts'), postData);
      }

      setShowPostModal(false);
      setEditingPost(null);
      setPostForm({ title: '', content: '', imageUrl: '' });
      await loadPosts(selectedVenueId);
      alert('Poszt sikeresen mentve!');
    } catch (error) {
      console.error('Error saving post:', error);
      alert('Hiba történt a poszt mentése során.');
    }
  };

  const deleteEvent = async (eventId: string) => {
    if (!window.confirm('Biztosan törli ezt az eseményt?')) return;

    try {
      // First, check if this is a tournament event and delete the associated tournament
      const eventDoc = await getDocs(query(collection(db, 'venueEvents'), where('__name__', '==', eventId)));
      if (!eventDoc.empty) {
        const eventData = eventDoc.docs[0].data();

        // If this is a tournament event, delete the associated tournament
        if (eventData.eventType === 'tournament') {
          const tournamentQuery = query(
            collection(db, 'tournaments'),
            where('venueEventId', '==', eventId)
          );
          const tournamentSnapshot = await getDocs(tournamentQuery);

          // Delete all associated tournaments
          const deletePromises = tournamentSnapshot.docs.map(tournamentDoc =>
            deleteDoc(doc(db, 'tournaments', tournamentDoc.id))
          );
          await Promise.all(deletePromises);

          console.log(`Deleted ${tournamentSnapshot.size} associated tournament(s)`);
        }
      }

      // Then delete (deactivate) the venue event
      await updateDoc(doc(db, 'venueEvents', eventId), {
        isActive: false,
        updatedAt: serverTimestamp()
      });

      await loadEvents(selectedVenueId);
      alert('Esemény és kapcsolódó verseny törölve!');
    } catch (error) {
      console.error('Error deleting event:', error);
      alert('Hiba történt a törlés során.');
    }
  };

  const deletePost = async (postId: string) => {
    if (!window.confirm('Biztosan törli ezt a posztot?')) return;

    try {
      await updateDoc(doc(db, 'venuePosts', postId), {
        isActive: false,
        updatedAt: serverTimestamp()
      });
      await loadPosts(selectedVenueId);
      alert('Poszt törölve!');
    } catch (error) {
      console.error('Error deleting post:', error);
      alert('Hiba történt a törlés során.');
    }
  };

  if (!isVenueOperator) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="card-dark p-8">
          <div className="text-center py-8">
            <p className="text-[#B8B8C3]">Ez a funkció csak üzemeltetők számára érhető el.</p>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="card-dark p-8">
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-400"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 space-y-6">
      <div className="card-dark p-8">
        <div className="mb-6">
          <h2 className="text-3xl font-bold text-gradient-cyan mb-2">{t('sidebar.customization')}</h2>
          <p className="text-[#B8B8C3]">{venueName} - Testreszabás</p>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-[#31383D]">
          <button
            onClick={() => setActiveTab('image')}
            className={`px-6 py-3 font-semibold transition-colors ${activeTab === 'image'
              ? 'text-cyan-400 border-b-2 border-cyan-400'
              : 'text-[#B8B8C3] hover:text-white'
              }`}
          >
            🖼️ Főkép
          </button>
          <button
            onClick={() => setActiveTab('events')}
            className={`px-6 py-3 font-semibold transition-colors ${activeTab === 'events'
              ? 'text-cyan-400 border-b-2 border-cyan-400'
              : 'text-[#B8B8C3] hover:text-white'
              }`}
          >
            🎯 Események
          </button>
          <button
            onClick={() => setActiveTab('posts')}
            className={`px-6 py-3 font-semibold transition-colors ${activeTab === 'posts'
              ? 'text-cyan-400 border-b-2 border-cyan-400'
              : 'text-[#B8B8C3] hover:text-white'
              }`}
          >
            📝 Posztok
          </button>
        </div>

        {/* Main Image Tab */}
        {activeTab === 'image' && (
          <div className="space-y-4">
            <h3 className="text-xl font-bold text-white mb-4">Klub főképe</h3>
            {mainImageUrl && (
              <div className="mb-4">
                <img src={mainImageUrl} alt="Venue main" className="w-full max-w-2xl rounded-lg" />
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-[#B8B8C3] mb-2">
                Új főkép feltöltése
              </label>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                disabled={uploadingImage}
                className="block w-full text-sm text-[#B8B8C3] file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-cyan-500 file:text-white hover:file:bg-cyan-600 disabled:opacity-50"
              />
              {uploadingImage && <p className="text-cyan-400 mt-2">Feltöltés...</p>}
            </div>
          </div>
        )}

        {/* Events Tab */}
        {activeTab === 'events' && (
          <div className="space-y-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-white">Események</h3>
              <button
                onClick={() => setShowEventModal(true)}
                className="px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-lg hover:shadow-[0_0_20px_rgba(6,182,212,0.5)] transition-all"
              >
                + Új esemény
              </button>
            </div>

            {events.length === 0 ? (
              <p className="text-[#B8B8C3] text-center py-8">Nincs esemény</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {events.map((event) => (
                  <div key={event.id} className="bg-[#1d232a] rounded-lg p-4 border border-[#31383D]">
                    <div className="flex justify-between items-start mb-2">
                      <h4 className="text-lg font-bold text-white">{event.title}</h4>
                      <span className="px-2 py-1 bg-cyan-500/20 text-cyan-400 text-xs rounded">
                        {event.eventType}
                      </span>
                    </div>
                    <p className="text-sm text-[#B8B8C3] mb-2">{event.description}</p>
                    <p className="text-xs text-[#8a8f99]">
                      {new Date(event.startDate.seconds * 1000).toLocaleDateString()} - {new Date(event.endDate.seconds * 1000).toLocaleDateString()}
                    </p>
                    <div className="flex gap-2 mt-3">
                      <button
                        onClick={() => {
                          setEditingEvent(event.id);
                          setEventForm({
                            title: event.title,
                            description: event.description,
                            eventType: event.eventType,
                            startDate: new Date(event.startDate.seconds * 1000).toISOString().split('T')[0],
                            endDate: new Date(event.endDate.seconds * 1000).toISOString().split('T')[0],
                            imageUrl: event.imageUrl || '',
                            registrationDeadline: '',
                            tournamentType: 'single_elimination',
                            gameMode: '501',
                            maxParticipants: 16,
                            prizePool: 0,
                            entryFee: 0,
                            rules: '',
                            firstPrize: '',
                            secondPrize: '',
                            thirdPrize: ''
                          });
                          setShowEventModal(true);
                        }}
                        className="flex-1 px-3 py-1 bg-cyan-500 text-white text-sm rounded hover:bg-cyan-600"
                      >
                        Szerkesztés
                      </button>
                      <button
                        onClick={() => deleteEvent(event.id)}
                        className="px-3 py-1 bg-red-500 text-white text-sm rounded hover:bg-red-600"
                      >
                        Törlés
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Posts Tab */}
        {activeTab === 'posts' && (
          <div className="space-y-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-white">Posztok</h3>
              <button
                onClick={() => setShowPostModal(true)}
                className="px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-lg hover:shadow-[0_0_20px_rgba(6,182,212,0.5)] transition-all"
              >
                + Új poszt
              </button>
            </div>

            {posts.length === 0 ? (
              <p className="text-[#B8B8C3] text-center py-8">Nincs poszt</p>
            ) : (
              <div className="space-y-4">
                {posts.map((post) => (
                  <div key={post.id} className="bg-[#1d232a] rounded-lg p-4 border border-[#31383D]">
                    <h4 className="text-lg font-bold text-white mb-2">{post.title}</h4>
                    <p className="text-sm text-[#B8B8C3] mb-3">{post.content}</p>
                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          setEditingPost(post.id);
                          setPostForm({
                            title: post.title,
                            content: post.content,
                            imageUrl: post.imageUrl || ''
                          });
                          setShowPostModal(true);
                        }}
                        className="px-3 py-1 bg-cyan-500 text-white text-sm rounded hover:bg-cyan-600"
                      >
                        Szerkesztés
                      </button>
                      <button
                        onClick={() => deletePost(post.id)}
                        className="px-3 py-1 bg-red-500 text-white text-sm rounded hover:bg-red-600"
                      >
                        Törlés
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Event Modal */}
      {showEventModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="card-dark rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto my-8">
            <h3 className="text-2xl font-bold text-white mb-6">
              {editingEvent ? 'Esemény szerkesztése' : 'Új esemény'}
            </h3>
            <form onSubmit={handleEventSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#B8B8C3] mb-2">Cím *</label>
                <input
                  type="text"
                  value={eventForm.title}
                  onChange={(e) => setEventForm({ ...eventForm, title: e.target.value })}
                  className="w-full px-4 py-2 bg-[#15191E] border border-[#31383D] rounded-lg text-white focus:outline-none focus:border-cyan-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#B8B8C3] mb-2">Leírás</label>
                <textarea
                  value={eventForm.description}
                  onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })}
                  className="w-full px-4 py-2 bg-[#15191E] border border-[#31383D] rounded-lg text-white focus:outline-none focus:border-cyan-500"
                  rows={3}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#B8B8C3] mb-2">Típus *</label>
                <select
                  value={eventForm.eventType}
                  onChange={(e) => setEventForm({ ...eventForm, eventType: e.target.value as any })}
                  className="w-full px-4 py-2 bg-[#15191E] border border-[#31383D] rounded-lg text-white focus:outline-none focus:border-cyan-500"
                >
                  <option value="tournament">Verseny (létrehoz egy valódi versenyt is)</option>
                  <option value="special">Különleges esemény</option>
                  <option value="promotion">Promóció</option>
                </select>
              </div>

              {/* Tournament-specific fields */}
              {eventForm.eventType === 'tournament' && (
                <div className="border-t border-[#31383D] pt-4 space-y-4">
                  <h4 className="text-lg font-semibold text-cyan-400">Verseny beállítások</h4>

                  <div>
                    <label className="block text-sm font-medium text-[#B8B8C3] mb-2">Regisztrációs határidő</label>
                    <input
                      type="date"
                      value={eventForm.registrationDeadline}
                      onChange={(e) => setEventForm({ ...eventForm, registrationDeadline: e.target.value })}
                      max={eventForm.startDate || '9999-12-31'}
                      className="w-full px-4 py-2 bg-[#15191E] border border-[#31383D] rounded-lg text-white focus:outline-none focus:border-cyan-500"
                    />
                    <p className="text-xs text-[#8a8f99] mt-1">
                      Ha üresen hagyod, automatikusan 3 nappal a kezdés előttre lesz állítva
                    </p>
                    {eventForm.registrationDeadline && eventForm.startDate && new Date(eventForm.registrationDeadline) > new Date(eventForm.startDate) && (
                      <p className="text-red-400 text-xs mt-1">⚠️ A határidő nem lehet későbbi, mint a kezdő dátum</p>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-[#B8B8C3] mb-2">Verseny típus</label>
                      <select
                        value={eventForm.tournamentType}
                        onChange={(e) => setEventForm({ ...eventForm, tournamentType: e.target.value as any })}
                        className="w-full px-4 py-2 bg-[#15191E] border border-[#31383D] rounded-lg text-white focus:outline-none focus:border-cyan-500"
                      >
                        <option value="single_elimination">Egyszerű kieséses</option>
                        <option value="double_elimination">Dupla kieséses</option>
                        <option value="round_robin">Körmérkőzés</option>
                        <option value="swiss">Svájci rendszer</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#B8B8C3] mb-2">Játékmód</label>
                      <select
                        value={eventForm.gameMode}
                        onChange={(e) => setEventForm({ ...eventForm, gameMode: e.target.value as any })}
                        className="w-full px-4 py-2 bg-[#15191E] border border-[#31383D] rounded-lg text-white focus:outline-none focus:border-cyan-500"
                      >
                        <option value="301">301</option>
                        <option value="501">501</option>
                        <option value="cricket">Cricket</option>
                        <option value="custom">Egyéni</option>
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-[#B8B8C3] mb-2">Max résztvevők</label>
                      <input
                        type="number"
                        value={eventForm.maxParticipants}
                        onChange={(e) => setEventForm({ ...eventForm, maxParticipants: parseInt(e.target.value) || 16 })}
                        className="w-full px-4 py-2 bg-[#15191E] border border-[#31383D] rounded-lg text-white focus:outline-none focus:border-cyan-500"
                        min="4"
                        step="4"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#B8B8C3] mb-2">Nevezési díj (Ft)</label>
                      <input
                        type="number"
                        value={eventForm.entryFee}
                        onChange={(e) => setEventForm({ ...eventForm, entryFee: parseInt(e.target.value) || 0 })}
                        className="w-full px-4 py-2 bg-[#15191E] border border-[#31383D] rounded-lg text-white focus:outline-none focus:border-cyan-500"
                        min="0"
                        step="500"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#B8B8C3] mb-2">Nyereményalap (Ft)</label>
                    <input
                      type="number"
                      value={eventForm.prizePool}
                      onChange={(e) => setEventForm({ ...eventForm, prizePool: parseInt(e.target.value) || 0 })}
                      className="w-full px-4 py-2 bg-[#15191E] border border-[#31383D] rounded-lg text-white focus:outline-none focus:border-cyan-500"
                      min="0"
                      step="1000"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#B8B8C3] mb-2">Verseny szabályok</label>
                    <textarea
                      value={eventForm.rules}
                      onChange={(e) => setEventForm({ ...eventForm, rules: e.target.value })}
                      className="w-full px-4 py-2 bg-[#15191E] border border-[#31383D] rounded-lg text-white focus:outline-none focus:border-cyan-500"
                      rows={3}
                      placeholder="Verseny szabályok és rendelkezések..."
                    />
                  </div>

                  <div className="border-t border-[#31383D] pt-4">
                    <h5 className="text-md font-semibold text-white mb-3">Nyeremények (opcionális)</h5>
                    <p className="text-xs text-[#8a8f99] mb-3">
                      Add meg, hogy az egyes helyezések milyen nyereményt kapnak (pl. "50.000 Ft" vagy "Profi darts szett")
                    </p>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-[#B8B8C3] mb-2">🥇 1. helyezés nyereménye</label>
                        <input
                          type="text"
                          value={eventForm.firstPrize}
                          onChange={(e) => setEventForm({ ...eventForm, firstPrize: e.target.value })}
                          className="w-full px-4 py-2 bg-[#15191E] border border-[#31383D] rounded-lg text-white focus:outline-none focus:border-cyan-500"
                          placeholder="pl. 50.000 Ft vagy Profi darts szett"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-[#B8B8C3] mb-2">🥈 2. helyezés nyereménye</label>
                        <input
                          type="text"
                          value={eventForm.secondPrize}
                          onChange={(e) => setEventForm({ ...eventForm, secondPrize: e.target.value })}
                          className="w-full px-4 py-2 bg-[#15191E] border border-[#31383D] rounded-lg text-white focus:outline-none focus:border-cyan-500"
                          placeholder="pl. 30.000 Ft vagy Darts táska"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-[#B8B8C3] mb-2">🥉 3. helyezés nyereménye</label>
                        <input
                          type="text"
                          value={eventForm.thirdPrize}
                          onChange={(e) => setEventForm({ ...eventForm, thirdPrize: e.target.value })}
                          className="w-full px-4 py-2 bg-[#15191E] border border-[#31383D] rounded-lg text-white focus:outline-none focus:border-cyan-500"
                          placeholder="pl. 10.000 Ft vagy Darts nyilak"
                        />
                      </div>
                    </div>
                    <p className="text-xs text-[#8a8f99] mt-2">
                      Ezeket később is be tudod állítani
                    </p>
                  </div>

                  <div className="bg-cyan-500/10 border border-cyan-500/20 rounded-lg p-3">
                    <p className="text-sm text-cyan-400">
                      ℹ️ Ez a verseny automatikusan létrejön a tournaments oldalon is, de csak a klub eseményeinél lesz látható.
                    </p>
                  </div>
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#B8B8C3] mb-2">Kezdő dátum *</label>
                  <input
                    type="date"
                    value={eventForm.startDate}
                    onChange={(e) => setEventForm({ ...eventForm, startDate: e.target.value })}
                    min={eventForm.registrationDeadline || undefined}
                    className="w-full px-4 py-2 bg-[#15191E] border border-[#31383D] rounded-lg text-white focus:outline-none focus:border-cyan-500"
                    required
                  />
                  {eventForm.registrationDeadline && eventForm.startDate && new Date(eventForm.startDate) < new Date(eventForm.registrationDeadline) && (
                    <p className="text-red-400 text-xs mt-1">⚠️ A kezdő dátum nem lehet korábbi, mint a regisztrációs határidő</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#B8B8C3] mb-2">Befejező dátum *</label>
                  <input
                    type="date"
                    value={eventForm.endDate}
                    onChange={(e) => setEventForm({ ...eventForm, endDate: e.target.value })}
                    min={eventForm.startDate || undefined}
                    className="w-full px-4 py-2 bg-[#15191E] border border-[#31383D] rounded-lg text-white focus:outline-none focus:border-cyan-500"
                    required
                  />
                  {eventForm.endDate && eventForm.startDate && new Date(eventForm.endDate) < new Date(eventForm.startDate) && (
                    <p className="text-red-400 text-xs mt-1">⚠️ A befejező dátum nem lehet korábbi, mint a kezdő dátum</p>
                  )}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#B8B8C3] mb-2">Kép URL (opcionális)</label>
                <input
                  type="url"
                  value={eventForm.imageUrl}
                  onChange={(e) => setEventForm({ ...eventForm, imageUrl: e.target.value })}
                  className="w-full px-4 py-2 bg-[#15191E] border border-[#31383D] rounded-lg text-white focus:outline-none focus:border-cyan-500"
                />
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  type="submit"
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-semibold rounded-lg hover:shadow-[0_0_20px_rgba(6,182,212,0.5)] transition-all"
                >
                  Mentés
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowEventModal(false);
                    setEditingEvent(null);
                    resetEventForm();
                  }}
                  className="flex-1 px-6 py-3 bg-[#31383D] text-white rounded-lg hover:bg-[#3d4758] transition-colors"
                >
                  Mégse
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Post Modal */}
      {showPostModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="card-dark rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto my-8">
            <h3 className="text-2xl font-bold text-white mb-6">
              {editingPost ? 'Poszt szerkesztése' : 'Új poszt'}
            </h3>
            <form onSubmit={handlePostSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#B8B8C3] mb-2">Cím *</label>
                <input
                  type="text"
                  value={postForm.title}
                  onChange={(e) => setPostForm({ ...postForm, title: e.target.value })}
                  className="w-full px-4 py-2 bg-[#15191E] border border-[#31383D] rounded-lg text-white focus:outline-none focus:border-cyan-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#B8B8C3] mb-2">Tartalom *</label>
                <textarea
                  value={postForm.content}
                  onChange={(e) => setPostForm({ ...postForm, content: e.target.value })}
                  className="w-full px-4 py-2 bg-[#15191E] border border-[#31383D] rounded-lg text-white focus:outline-none focus:border-cyan-500"
                  rows={5}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#B8B8C3] mb-2">Kép URL (opcionális)</label>
                <input
                  type="url"
                  value={postForm.imageUrl}
                  onChange={(e) => setPostForm({ ...postForm, imageUrl: e.target.value })}
                  className="w-full px-4 py-2 bg-[#15191E] border border-[#31383D] rounded-lg text-white focus:outline-none focus:border-cyan-500"
                />
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  type="submit"
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-semibold rounded-lg hover:shadow-[0_0_20px_rgba(6,182,212,0.5)] transition-all"
                >
                  Mentés
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowPostModal(false);
                    setEditingPost(null);
                    setPostForm({ title: '', content: '', imageUrl: '' });
                  }}
                  className="flex-1 px-6 py-3 bg-[#31383D] text-white rounded-lg hover:bg-[#3d4758] transition-colors"
                >
                  Mégse
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default VenueCustomization;
